/*
  MOA · CORRECTOR DEL BUSCADOR
  Fichero de MOA-Grafics. NO es del proveedor del tema.

  QUE HACE
  --------
  Cuando una busqueda se queda a cero, la vuelve a intentar con lo que el
  comprador probablemente queria escribir, y lo dice en pantalla. Nada mas.

  LA REGLA QUE LO GOBIERNA TODO: SOLO ACTUA SOBRE CONSULTAS QUE YA DABAN CERO.
  --------------------------------------------------------------------------
  No es una preferencia de estilo, es lo que hace que esto no pueda estropear
  nada. Si la consulta devuelve algo, este fichero no se entera de que existe.

  LO QUE SE MIDIO ANTES DE ESCRIBIRLO (2026-09-08, tienda viva)
  -------------------------------------------------------------
  El buscador de Shopify **ya pliega las tildes y ya perdona una errata**:
  `jagermeister` sin dieresis devuelve los 3 parasoles, y `porshe`, `mercedez`
  y `peugot` devuelven producto. Lo que NO perdona:

    - la palabra acentuada cortada -> `jager`, `raikko`, `maki`      = 0
    - tilde mas errata a la vez    -> `jagermaister`, `nurburgrin`   = 0
    - dos o mas erratas            -> `pujot`, `suvaru`, `parazoles` = 0
    - el prefijo rinde poco        -> `stick` daba 2 de 44 pegatinas

  Y el hallazgo que decide el diseno: **el comodin `*` SI pliega las tildes.**
  `jager*` devuelve 3 y `jager` cero. Por eso el primer intento del rescate es
  el comodin, que no necesita datos ni mantenimiento.

  Pero `*` **no siempre mejora**: `porshe` baja de 7 a 4 y `mercedez` de 10 a 9.
  Por eso NO se pone siempre: solo en el rescate.

  POR QUE NO PARCHEA EL COMPONENTE DEL TEMA
  -----------------------------------------
  `assets/search.js` define el elemento `predictive-search`. Se penso en
  envolver su `getQuery()`, y se descarto: es un metodo del proveedor, y si una
  actualizacion lo renombra el parche deja de aplicarse **en silencio**, que es
  el peor fallo posible. Aqui se mira el DOM. Si el proveedor cambia
  `.search__empty` o `.empty-state`, esto deja de actuar y la tienda se comporta
  como antes de existir el fichero.

  POR QUE NO SE REESCRIBE LA CAJA DE BUSQUEDA
  -------------------------------------------
  Quien escribe «jagermeister» pasa por `j`, `ja`, `jag`, y todos dan cero.
  Reescribirle el texto a media palabra es el defecto que la gente odia del
  corrector del movil. En el desplegable se cambian los resultados y **nunca lo
  que el comprador ha escrito**.
*/

(function () {
  'use strict';

  var MARCA = 'moa-buscador';
  var MIN_LARGO = 4; // por debajo no se toca: gti, r34, e30, jdm, f1 son etiquetas reales
  var PARAM_DE = 'moa_de'; // «venimos de esta consulta»: tambien prohibe corregir otra vez
  var PARAM_CRUDO = 'moa_x'; // «el comprador pidio la consulta literal»

  // Los signos que sobran al descomponer una letra acentuada. Se escribe con
  // codigos y no con los caracteres sueltos: un acento suelto en un fichero
  // sobrevive mal a cualquier conversion de codificacion.
  var SIGNOS = new RegExp('[\\u0300-\\u036f]', 'g');

  var raiz = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var vocabulario = null; // {plegada: [grafia, frecuencia]}
  var sinonimos = {}; // {palabra que nadie encuentra: palabra del catalogo}
  var vocabularioPedido = null;

  // ---------------------------------------------------------------- utilidades

  function plegar(texto) {
    // Misma normalizacion que el generador del vocabulario.
    return texto
      .toLowerCase()
      .normalize('NFD')
      .replace(SIGNOS, '');
  }

  function trocear(consulta) {
    return consulta.split(/[^\p{L}\p{N}]+/u).filter(function (t) {
      return t.length > 0;
    });
  }

  function urlVocabulario() {
    var marca = document.getElementById(MARCA);
    return marca ? marca.getAttribute('data-vocabulario') : null;
  }

  function cargarVocabulario() {
    // Perezoso a proposito: en una visita que no se queda a cero no se descarga
    // ni un byte.
    if (vocabularioPedido) return vocabularioPedido;
    var url = urlVocabulario();
    if (!url) return Promise.resolve(null);
    vocabularioPedido = fetch(url)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (datos) {
        vocabulario = datos && datos.t ? datos.t : null;
        sinonimos = (datos && datos.s) || {};
        return vocabulario;
      })
      .catch(function () {
        return null;
      });
    return vocabularioPedido;
  }

  // Damerau-Levenshtein con corte: en cuanto la fila entera pasa del limite se
  // abandona, que es lo que permite recorrer 576 terminos sin que se note.
  function distancia(a, b, limite) {
    if (Math.abs(a.length - b.length) > limite) return limite + 1;
    var anterior2 = null;
    var anterior = [];
    var i;
    for (i = 0; i <= b.length; i++) anterior[i] = i;
    for (i = 1; i <= a.length; i++) {
      var actual = [i];
      var mejor = i;
      for (var j = 1; j <= b.length; j++) {
        var coste = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var valor = Math.min(anterior[j] + 1, actual[j - 1] + 1, anterior[j - 1] + coste);
        if (
          anterior2 &&
          i > 1 &&
          j > 1 &&
          a.charAt(i - 1) === b.charAt(j - 2) &&
          a.charAt(i - 2) === b.charAt(j - 1)
        ) {
          valor = Math.min(valor, anterior2[j - 2] + 1);
        }
        actual[j] = valor;
        if (valor < mejor) mejor = valor;
      }
      if (mejor > limite) return limite + 1;
      anterior2 = anterior;
      anterior = actual;
    }
    return anterior[b.length];
  }

  function mejorCandidato(token) {
    if (!vocabulario) return null;
    var clave = plegar(token);
    if (Object.prototype.hasOwnProperty.call(vocabulario, clave)) return null; // ya es del catalogo

    // 1) Prefijo. Es lo que salva la palabra a medias que ademas lleva tilde.
    //    Gana el candidato mas corto, y a igual largo el mas frecuente.
    var porPrefijo = null;
    // 2) Distancia de edicion. Uno para palabras cortas, dos para largas: con
    //    dos sobre una palabra de cuatro letras casi todo se parece a todo.
    var limite = clave.length <= 5 ? 1 : 2;
    var porDistancia = null;
    var mejorDistancia = limite + 1;

    for (var candidato in vocabulario) {
      if (!Object.prototype.hasOwnProperty.call(vocabulario, candidato)) continue;
      var frecuencia = vocabulario[candidato][1];
      if (candidato.length > clave.length && candidato.indexOf(clave) === 0) {
        if (
          !porPrefijo ||
          candidato.length < porPrefijo.clave.length ||
          (candidato.length === porPrefijo.clave.length && frecuencia > porPrefijo.frecuencia)
        ) {
          porPrefijo = { clave: candidato, frecuencia: frecuencia };
        }
        continue;
      }
      var d = distancia(clave, candidato, limite);
      if (d <= limite && (d < mejorDistancia || (d === mejorDistancia && porDistancia && frecuencia > porDistancia.frecuencia))) {
        mejorDistancia = d;
        porDistancia = { clave: candidato, frecuencia: frecuencia };
      }
    }

    var elegido = porPrefijo || porDistancia;
    return elegido ? vocabulario[elegido.clave][0] : null;
  }

  function conComodin(consulta) {
    var tokens = trocear(consulta);
    if (!tokens.length) return null;
    var cambiado = false;
    var salida = tokens.map(function (t) {
      if (t.length < MIN_LARGO) return t;
      cambiado = true;
      return t + '*';
    });
    return cambiado ? salida.join(' ') : null;
  }

  function conVocabulario(consulta) {
    var tokens = trocear(consulta);
    if (!tokens.length) return null;
    var cambiado = false;
    var salida = tokens.map(function (t) {
      if (t.length < MIN_LARGO) return t;
      // El catalogo escribe «Parasol» en singular, asi que «parazoles» no se
      // parece a nada. Quitandole el plural queda «parazol», que esta a una
      // letra de «parasol». Shopify ya lematiza al buscar, de modo que mandar
      // el singular devuelve tambien los plurales.
      var mejor = mejorCandidato(t);
      if (!mejor && /es$/.test(t) && t.length > MIN_LARGO + 1) mejor = mejorCandidato(t.slice(0, -2));
      if (!mejor && /s$/.test(t) && t.length > MIN_LARGO) mejor = mejorCandidato(t.slice(0, -1));
      if (mejor && plegar(mejor) !== plegar(t)) {
        cambiado = true;
        return mejor;
      }
      return t;
    });
    return cambiado ? salida.join(' ') : null;
  }

  /*
    LOS SINONIMOS LOS APLICAMOS NOSOTROS, Y NO DEBERIA HACER FALTA.

    Mou dio de alta 24 grupos en el panel de Search & Discovery. **Estan
    inertes**, medido el 2026-09-08: `quitasol` esta en el mismo grupo que
    `parasol`, y parasol devuelve 241 mientras quitasol devuelve cero. El propio
    panel avisa de que Shopify pauso la funcion y ahora pone sinonimos comunes
    por su cuenta —por eso `print` y `wrap` si encuentran, sin estar en ningun
    texto del catalogo—.

    Aqui solo se traduce hacia palabras que **existen** en el catalogo. Los
    grupos de llaveros, cantimploras y lonas no producen nada, y esta bien: hoy
    no se venden.

    Si Shopify reactiva sus sinonimos, esto sobra y se quita del generador.
  */
  function conSinonimo(consulta) {
    var entera = plegar(consulta).replace(/\s+/g, ' ').trim();
    if (Object.prototype.hasOwnProperty.call(sinonimos, entera)) return sinonimos[entera];
    var tokens = trocear(consulta);
    if (!tokens.length) return null;
    var cambiado = false;
    var salida = tokens.map(function (t) {
      var clave = plegar(t);
      if (Object.prototype.hasOwnProperty.call(sinonimos, clave)) {
        cambiado = true;
        return sinonimos[clave];
      }
      return t;
    });
    return cambiado ? salida.join(' ') : null;
  }

  function candidatos(consulta, palabraPrimero) {
    // El comodin no depende de datos y tapa el caso mas comun —la palabra a
    // medias—, asi que en el desplegable va primero.
    //
    // EN LA PAGINA DE RESULTADOS VA AL REVES, y no es un capricho. Ahi la
    // consulta acaba en la direccion y el tema la escribe en el titulo: con el
    // comodin delante el comprador lee «3 resultados encontrados para "jager*"»,
    // con el asterisco a la vista. La palabra del catalogo da «jägermeister»,
    // que es lo que queria escribir. Medido el 2026-09-08.
    var porComodin = [];
    var porPalabra = [];

    var comodin = conComodin(consulta);
    if (comodin) porComodin.push({ consulta: comodin, mostrar: consulta });

    var corregida = conVocabulario(consulta);
    if (corregida) {
      porPalabra.push({ consulta: corregida, mostrar: corregida });
      var mixta = conComodin(corregida);
      if (mixta) porPalabra.push({ consulta: mixta, mostrar: corregida });
    }

    // El sinonimo va SIEMPRE el primero: es el unico de los tres que sabe lo
    // que el comprador queria decir, no solo como se escribe.
    var porSinonimo = [];
    var equivalente = conSinonimo(consulta);
    if (equivalente) porSinonimo.push({ consulta: equivalente, mostrar: equivalente });

    var resto = palabraPrimero ? porPalabra.concat(porComodin) : porComodin.concat(porPalabra);
    return porSinonimo.concat(resto);
  }

  function sinComodin(texto) {
    return texto.replace(/\*/g, '').replace(/\s+/g, ' ').trim();
  }

  function hayProductos(consulta) {
    var url =
      raiz +
      'search/suggest.json?q=' +
      encodeURIComponent(consulta) +
      '&resources[type]=product&resources[limit]=4';
    return fetch(url)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (datos) {
        try {
          return datos.resources.results.products.length > 0;
        } catch (e) {
          return false;
        }
      })
      .catch(function () {
        return false;
      });
  }

  // ------------------------------------------- la busqueda que trae lo que no es

  /*
    EL OTRO FALLO, Y ES EL CONTRARIO DEL ANTERIOR.

    Shopify busca dentro de la descripcion entera y une los terminos con O, asi
    que hay palabras que traen producto sin venir a cuento. Medido el 2026-09-08:
    «camiseta» devuelve un pack de pegatinas porque una descripcion dice «la que
    nadie pone en las camisetas», y «gorra» devuelve cinco porque describen un
    cerdo con gorra de policia. Son ocho fichas en total.

    NO SE ARREGLA CON UNA LISTA DE «ESTO NO LO VENDEMOS». Orden de Mou, y es la
    parte importante: el dia que haya camisetas tienen que salir. Asi que la
    senal se lee del catalogo y se actualiza sola.

    Palabra fuerte = la usa el catalogo para NOMBRAR algo: esta en un titulo, en
    un tipo de producto, en una etiqueta o en el proveedor. Es el mismo
    vocabulario del rescate. En cuanto exista una ficha llamada «Camiseta …», la
    palabra pasa a fuerte y este aviso desaparece sin tocar una linea de codigo.

    Y HACE FALTA UNA SEGUNDA CONDICION, medida:

      - `casco` devuelve 54 fichas, y **es legitimo**: la guia de aplicacion
        dice donde se pega el vinilo —«lunas y carroceria, paragolpes, casco,
        deposito de moto, portatil»—. Quien busca casco encuentra algo que le
        sirve.
      - `pegatina` devuelve 44 por un sinonimo dado de alta en el panel, que
        este fichero **no puede leer**: no hay recurso publico para eso.

    Las dos son palabras que el catalogo no usa para nombrar, y las dos son
    buenas. Lo que las separa del ruido es cuanto traen: `camiseta` una,
    `gorra` cinco, `chaqueta` una. **Por debajo de nueve resultados y sin estar
    en el vocabulario, la coincidencia es incidental.** Por encima, la palabra
    describe algo que el catalogo hace de verdad.
  */

  var TECHO_INCIDENTAL = 8;

  function algunaPalabraEsDelCatalogo(consulta) {
    if (!vocabulario) return true; // sin vocabulario no se acusa a nadie
    var tokens = trocear(consulta).filter(function (t) {
      return t.length >= MIN_LARGO;
    });
    if (!tokens.length) return true;
    for (var i = 0; i < tokens.length; i++) {
      if (Object.prototype.hasOwnProperty.call(vocabulario, plegar(tokens[i]))) return true;
      // Y tambien vale si se PARECE a una palabra del catalogo. Medido: sin
      // esto, `peugot` y `mitsubichi` —erratas de marcas que ya encuentran
      // producto— se llevaban el aviso de «ningun producto se llama asi», que
      // es cierto pero inutil: el comprador ya tiene delante lo que buscaba.
      if (mejorCandidato(tokens[i])) return true;
    }
    return false;
  }

  function cuantosProductos(consulta) {
    var url =
      raiz +
      'search/suggest.json?q=' +
      encodeURIComponent(consulta) +
      '&resources[type]=product&resources[limit]=10';
    return fetch(url)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (datos) {
        try {
          return datos.resources.results.products.length;
        } catch (e) {
          return 0;
        }
      })
      .catch(function () {
        return 0;
      });
  }

  function cuantosEnLaPagina(consulta) {
    // El desplegable se queda en diez, y aqui hay que comparar 142 contra 55.
    return fetch(raiz + 'search?q=' + encodeURIComponent(consulta) + '&type=product')
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var texto = doc.querySelector('#ProductCount');
        if (!texto) return 0;
        var m = texto.textContent.match(/\d+/);
        return m ? parseInt(m[0], 10) : 0;
      })
      .catch(function () {
        return 0;
      });
  }

  function avisoIncidental(consulta) {
    var p = document.createElement('p');
    p.className = 'moa-buscador-aviso moa-buscador-aviso--flojo';
    p.setAttribute('role', 'status');
    p.textContent =
      'Ningún producto se llama «' + consulta + '». Esto es lo más parecido que tenemos.';
    return p;
  }

  // ------------------------------------------------------------- el desplegable

  function urlSugerencias(consulta, seccion, limite) {
    var base =
      window.theme && theme.routes && theme.routes.predictive_search_url
        ? (theme.routes.shop_url || '') + theme.routes.predictive_search_url
        : raiz + 'search/suggest';
    return (
      base +
      '?q=' +
      encodeURIComponent(consulta) +
      '&resources[limit]=' +
      limite +
      '&resources[limit_scope]=each&section_id=' +
      encodeURIComponent(seccion)
    );
  }

  function avisoDesplegable(original) {
    var p = document.createElement('p');
    p.className = 'moa-buscador-aviso';
    p.setAttribute('role', 'status');
    p.textContent = 'Resultados aproximados para «' + original + '»';
    return p;
  }

  function rescatarDesplegable(contenedor) {
    var formulario = contenedor.closest('form');
    if (!formulario) return;
    var caja = formulario.querySelector('input[type="search"]');
    if (!caja) return;
    var consulta = caja.value.trim();
    if (consulta.length < MIN_LARGO) return;
    if (contenedor.dataset.moaRescatado === consulta) return;
    contenedor.dataset.moaRescatado = consulta;

    var seccion = contenedor.id.replace('PredictiveSearchResults-', '');
    var limite = parseInt(formulario.getAttribute('data-limit'), 10) || 3;

    cargarVocabulario().then(function () {
      probarEnElDesplegable(contenedor, caja, seccion, limite, consulta, candidatos(consulta));
    });
  }

  // `forzar` distingue los dos motivos por los que se entra aqui:
  //   - sin forzar: el desplegable no traia productos, y hay que respetar los
  //     que el tema pueda haber traido mientras tanto;
  //   - forzando: el desplegable traia dos resultados flojos y el sinonimo trae
  //     mas, asi que sustituirlos es justo lo que se quiere.
  function probarEnElDesplegable(contenedor, caja, seccion, limite, consulta, lista, forzar) {
    if (!lista.length) return;
    var intento = 0;
    function siguiente() {
      if (intento >= lista.length) return;
      var actual = lista[intento++];
      fetch(urlSugerencias(actual.consulta, seccion, limite))
        .then(function (r) {
          return r.ok ? r.text() : null;
        })
        .then(function (html) {
          if (!html) return siguiente();
          // Si el comprador ha seguido escribiendo, esto ya no vale.
          if (caja.value.trim() !== consulta) return;
          // Y si mientras tanto el tema ha traido productos de verdad, mandan
          // los suyos: nunca se pisa un resultado bueno con uno aproximado.
          if (!forzar && !contenedor.querySelector('.moa-buscador-aviso') && !sinProductos(contenedor)) return;
          var traido = new DOMParser()
            .parseFromString(html, 'text/html')
            .getElementById(contenedor.id);
          // Tiene que traer PRODUCTOS. Sin esto, `llavero` se daba por rescatado
          // porque el desplegable devolvia la coleccion «Llaveros» y ningun
          // producto: el aviso salia encima de una caja vacia.
          if (!traido || !traido.querySelector('a[href*="/products/"]')) return siguiente();
          contenedor.replaceChildren.apply(
            contenedor,
            Array.prototype.slice.call(traido.childNodes)
          );
          contenedor.insertBefore(avisoDesplegable(consulta), contenedor.firstChild);
        })
        .catch(function () {
          /* sin rescate, se queda lo que hubiera */
        });
    }
    siguiente();
  }

  function marcarIncidental(contenedor) {
    var formulario = contenedor.closest('form');
    if (!formulario) return;
    var caja = formulario.querySelector('input[type="search"]');
    if (!caja) return;
    var consulta = caja.value.trim();
    if (consulta.length < MIN_LARGO) return;
    // Si ya hay aviso es el del rescate, y ese manda: no se apilan dos.
    if (contenedor.querySelector('.moa-buscador-aviso')) return;
    if (contenedor.dataset.moaFlojo === consulta) return;
    contenedor.dataset.moaFlojo = consulta;

    cargarVocabulario().then(function () {
      // Mismo orden que en la pagina: el sinonimo manda sobre el parecido.
      var equivalente = conSinonimo(consulta);

      cuantosProductos(consulta).then(function (cuantos) {
        if (!cuantos) return;
        if (caja.value.trim() !== consulta) return;
        if (contenedor.querySelector('.search__empty')) return;
        if (contenedor.querySelector('.moa-buscador-aviso')) return;

        if (equivalente) {
          return cuantosProductos(equivalente).then(function (despues) {
            if (despues <= cuantos) return;
            var seccion = contenedor.id.replace('PredictiveSearchResults-', '');
            var formulario = contenedor.closest('form');
            var limite = parseInt(formulario.getAttribute('data-limit'), 10) || 3;
            probarEnElDesplegable(
              contenedor,
              caja,
              seccion,
              limite,
              consulta,
              [{ consulta: equivalente, mostrar: equivalente }],
              true
            );
          });
        }

        if (cuantos > TECHO_INCIDENTAL) return;
        if (algunaPalabraEsDelCatalogo(consulta)) return;
        contenedor.insertBefore(avisoIncidental(consulta), contenedor.firstChild);
      });
    });
  }

  function sinProductos(contenedor) {
    return !contenedor.querySelector('a[href*="/products/"]');
  }

  function atenderDesplegable(contenedor) {
    // MIENTRAS EL TEMA ESTA BUSCANDO NO SE TOCA NADA, o se rescata un vacio que
    // en medio segundo iba a llenarse solo.
    var formulario = contenedor.closest('form');
    if (formulario && formulario.hasAttribute('loading')) return;

    // NO BASTA CON MIRAR EL BLOQUE DE «SIN RESULTADOS», y esto costo una prueba:
    // al escribir `quitasol` el desplegable enseñaba la coleccion «Parasoles» y
    // **ningun producto**, asi que no habia bloque vacio y el rescate no
    // entraba. Lo que le importa al comprador son los productos.
    if (contenedor.querySelector('.search__empty') || sinProductos(contenedor)) {
      rescatarDesplegable(contenedor);
    } else {
      marcarIncidental(contenedor);
    }
  }

  // --------------------------------------------------- la pagina de resultados

  function avisoPagina(original, usada) {
    var caja = document.createElement('div');
    caja.className = 'moa-buscador-aviso moa-buscador-aviso--pagina';
    caja.setAttribute('role', 'status');
    var texto = document.createElement('span');
    texto.textContent = 'Mostrando resultados para «' + sinComodin(usada) + '».';
    var enlace = document.createElement('a');
    enlace.href = raiz + 'search?q=' + encodeURIComponent(original) + '&' + PARAM_CRUDO + '=1';
    enlace.textContent = 'Buscar «' + original + '» en su lugar';
    caja.appendChild(texto);
    caja.appendChild(document.createTextNode(' '));
    caja.appendChild(enlace);
    return caja;
  }

  function arrancarPagina() {
    var parametros = new URLSearchParams(window.location.search);
    var consulta = (parametros.get('q') || '').trim();
    if (!consulta) return;

    var original = parametros.get(PARAM_DE);
    if (original) {
      // Ya venimos corregidos: solo hay que decirlo. Y NO se vuelve a corregir.
      var destino = document.getElementById('ProductGridContainer');
      if (destino && !destino.querySelector('.moa-buscador-aviso')) {
        destino.insertBefore(avisoPagina(original, consulta), destino.firstChild);
      }
      return;
    }
    if (parametros.get(PARAM_CRUDO)) return; // lo pidio literal
    if (consulta.length < MIN_LARGO) return;

    if (!document.querySelector('.empty-state')) {
      // Hay resultados. Lo unico que puede pasar aqui es que no vengan a cuento,
      // y ni siquiera entonces se quita ninguno.
      var texto = document.querySelector('#ProductCount');
      var cuantos = texto ? parseInt((texto.textContent.match(/\d+/) || [0])[0], 10) : 0;
      if (!cuantos) return;

      cargarVocabulario().then(function () {
        function avisar() {
          if (cuantos > TECHO_INCIDENTAL) return;
          if (algunaPalabraEsDelCatalogo(consulta)) return;
          var destino = document.getElementById('ProductGridContainer');
          if (destino && !destino.querySelector('.moa-buscador-aviso')) {
            var nodo = avisoIncidental(consulta);
            nodo.className += ' moa-buscador-aviso--pagina';
            destino.insertBefore(nodo, destino.firstChild);
          }
        }

        // EL SINONIMO SE MIRA ANTES QUE EL PARECIDO, y esto costo una prueba:
        // `lamina` traia dos resultados y no se rescataba, porque el corrector
        // la daba por palabra del catalogo al parecerse a «laminado». Un
        // sinonimo escrito a mano dice lo que el comprador QUERIA decir; un
        // parecido solo dice como lo escribio.
        var equivalente = conSinonimo(consulta);
        if (!equivalente) return avisar();

        // Y solo se cambia si trae MAS. Sin esta comprobacion, `print` —142
        // resultados por los sinonimos automaticos de Shopify— acabaria
        // llevando a `poster`, que trae 55. Nunca se le quitan resultados a
        // nadie.
        cuantosEnLaPagina(equivalente).then(function (despues) {
          if (despues <= cuantos) return avisar();
          var destino = new URL(window.location.href);
          destino.searchParams.set('q', equivalente);
          destino.searchParams.set(PARAM_DE, consulta);
          window.location.replace(destino.toString());
        });
      });
      return;
    }

    cargarVocabulario().then(function () {
      var lista = candidatos(consulta, true);
      if (!lista.length) return;

      var intento = 0;
      function siguiente() {
        if (intento >= lista.length) return;
        var actual = lista[intento++];
        hayProductos(actual.consulta).then(function (hay) {
          if (!hay) return siguiente();
          var url = new URL(window.location.href);
          url.searchParams.set('q', actual.consulta);
          url.searchParams.set(PARAM_DE, consulta);
          // `replace` y no `assign`: el boton Atras tiene que devolver al
          // comprador a donde estaba, no a esta misma pagina vacia.
          window.location.replace(url.toString());
        });
      }
      siguiente();
    });
  }

  // ------------------------------------------------------------------ arranque

  function revisar(raizDom) {
    var contenedores = raizDom.querySelectorAll
      ? raizDom.querySelectorAll('[id^="PredictiveSearchResults-"]')
      : [];
    for (var i = 0; i < contenedores.length; i++) atenderDesplegable(contenedores[i]);
    if (raizDom.matches && raizDom.matches('[id^="PredictiveSearchResults-"]')) {
      atenderDesplegable(raizDom);
    }
  }

  function arrancar() {
    try {
      arrancarPagina();
    } catch (e) {
      /* la pagina sigue funcionando sin esto */
    }

    // No depende del orden de carga: da igual que `search.js` llegue antes o
    // despues, porque lo que se vigila es el DOM.
    var observador = new MutationObserver(function (cambios) {
      for (var i = 0; i < cambios.length; i++) {
        var anadidos = cambios[i].addedNodes;
        for (var j = 0; j < anadidos.length; j++) {
          if (anadidos[j].nodeType === 1) revisar(anadidos[j]);
        }
        if (cambios[i].target && cambios[i].target.nodeType === 1) {
          var contenedor = cambios[i].target.closest('[id^="PredictiveSearchResults-"]');
          if (contenedor) atenderDesplegable(contenedor);
        }
      }
    });
    observador.observe(document.body, { childList: true, subtree: true });
    revisar(document);
  }

  // Asa de comprobacion. No la usa nada del funcionamiento normal: esta para
  // poder pasar la bateria de consultas desde la consola del navegador sin
  // teclear una por una, que es como se verifica esto de verdad.
  //   await moaBuscador.cargar(); moaBuscador.candidatos('jager')
  window.moaBuscador = {
    plegar: plegar,
    distancia: distancia,
    candidatos: candidatos,
    cargar: cargarVocabulario,
    get vocabulario() {
      return vocabulario;
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
})();
